﻿import {
  Avatar,
  Typography,
  IconButton,
  Modal,
  Box,
  Tooltip,
} from '@mui/material';
import '../headerStyles.css';
import React, { Fragment, Suspense, lazy, useEffect, useState } from 'react';
import ProfileMenu from '@/components/ProfileMenu';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { SwitchApiRes, SwitchReq, LicenseApiRes, LicenseReq } from '../types';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getLicenceKey, switchCompany } from '../queries';
import { customAvatar, setCommonStorage } from '@/utils/common';
import { loginSuccess } from '@/views/login/store/reducers/user';
import { fetchCompanies, fetchDivisions } from '@/views/login/store/action';
import { useUserDateFormatter } from '@/hooks/useUserDateFormatter';
import dayjs from 'dayjs';
import BusinessIcon from '@mui/icons-material/Business';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CloseIcon from '@mui/icons-material/Close';
import NotificationBell from './NotificationBell';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { useNavigate } from 'react-router-dom';

// loaded when first opened, not with every page
const SwitchCompanyForm = lazy(() => import('./SwitchCompanyForm'));
const QRCode = lazy(() => import('react-qr-code'));

const HeaderContent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [switchUsed, setSwitchUsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openQR, setOpenQR] = useState(false);
  const [licKey, setLicKey] = useState('');
  const { loggedUserData } = useAppSelector((state) => state.loginReducer);
  const { companyData } = useAppSelector((state) => state.companyReducer);
  const { divisionData } = useAppSelector((state) => state.divisionReducer);
  const loggedUserName = loggedUserData?.user_code;
  const loggedUserCompCode = loggedUserData?.comp_code;
  const loggedUserCompName = loggedUserData?.comp_name;
  const loggedUserDivsnCode = loggedUserData?.divn_code;
  const loggedUserDivsnName = loggedUserData?.divn_name;
  const { formatDate } = useUserDateFormatter();
  //const open = Boolean(anchorEl);

  const { mutate: getToken } = useMutation<
    AxiosResponse<SwitchApiRes>,
    Error,
    SwitchReq
  >({
    mutationKey: ['switchUserCompany'],
    mutationFn: switchCompany,
    onSuccess(resp) {
      const token = resp.data.result?.token;
      const jwt = setCommonStorage(token);
      dispatch(loginSuccess({ loggedUserData: jwt }));
    },
  });

  const { mutate: getLicenceKeyDetails } = useMutation<
    AxiosResponse<LicenseApiRes>,
    Error,
    LicenseReq
  >({
    mutationKey: ['getCompanyData'],
    mutationFn: getLicenceKey,
    onSuccess(resp: any) {
      setLicKey(resp.result[0].LIC_KEY);
    },
  });

  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(fetchDivisions({ comp_code: loggedUserCompCode }));
  }, [loggedUserData, dispatch, loggedUserCompCode]);

  const handleDivisionClick = () => {
    setSwitchUsed(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickQR = () => {
    setOpenQR(true);
    const Lic_Payload: any = {
      comp_code: loggedUserCompCode,
      lic_type: import.meta.env.APP_LIC_TYPE,
      app_name: import.meta.env.APP_LIC_APP_NAME,
    };
    getLicenceKeyDetails(Lic_Payload);
  };

  return (
    <Fragment>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: { xs: 1, md: 2 },
          minWidth: 0,
        }}
      >
        {/* Left side - Company/Division Info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            minWidth: 0,
            px: { xs: 0, md: 1 },
            py: 0.75,
            borderRadius: '12px',
            border: { xs: 'none', md: '1px solid rgba(255, 255, 255, 0.08)' },
            bgcolor: { xs: 'transparent', md: 'rgba(255, 255, 255, 0.04)' },
          }}
        >
          {/* Company Icon */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: 'rgba(26, 188, 85, 0.15)',
              // decorative: hidden on phones to leave room for the names
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(26, 188, 85, 0.2)',
            }}
          >
            <BusinessIcon sx={{ color: '#1ABC55', fontSize: '1.375rem' }} />
          </Box>

          {/* Company & Division Text */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: 'inherit',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: 1.4,
                // phones: the screen less the menu button and the icons on the right
                maxWidth: { xs: 'calc(100vw - 252px)', sm: 220, lg: 320 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {loggedUserCompName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  // phones: the screen less the menu button and the icons on the right
                maxWidth: { xs: 'calc(100vw - 252px)', sm: 220, lg: 320 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {loggedUserDivsnName}
              </Typography>
              <Tooltip title="Switch Company/Division">
                <IconButton
                  onClick={handleDivisionClick}
                  size="small"
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: 'rgba(26, 188, 85, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(26, 188, 85, 0.2)',
                    },
                  }}
                >
                  <SwapHorizIcon sx={{ color: '#1ABC55', fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Right side - Actions & User Info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1 },
            flexShrink: 0,
            p: 0.5,
            borderRadius: '14px',
            bgcolor: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          {/* Notification Bell */}
          <NotificationBell />

          {/* ERP Academy: process guides */}
          <Tooltip title="ERP Academy — learn how each process works">
            <IconButton
              aria-label="ERP Academy"
              onClick={() => navigate('/academy')}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '9px',
                bgcolor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.12)',
                },
              }}
            >
              <SchoolOutlinedIcon sx={{ color: '#ffffff', fontSize: '1.375rem' }} />
            </IconButton>
          </Tooltip>

          {/* QR Code Button */}
          <Tooltip title="View QR Code">
            <IconButton
              onClick={handleClickQR}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '9px',
                bgcolor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.12)',
                },
              }}
            >
              <QrCode2Icon sx={{ color: '#ffffff', fontSize: '1.375rem' }} />
            </IconButton>
          </Tooltip>

          {/* Divider */}
          <Box
            sx={{
              width: '1px',
              height: '30px',
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              display: { xs: 'none', sm: 'block' },
            }}
          />

          {/* User Avatar & Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              py: 0.5,
              pl: 0.5,
              pr: { xs: 0.5, md: 1.25 },
              borderRadius: '10px',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
            onClick={handleClick}
          >
            <Avatar
              {...customAvatar(loggedUserName ?? '')}
              sx={{
                width: 40,
                height: 40,
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: '#1ABC55',
                border: '2px solid rgba(26, 188, 85, 0.3)',
              }}
            />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                sx={{
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  lineHeight: 1.4,
                }}
              >
                Hi, {loggedUserName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'inherit',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.5)',
                }}
              >
                Last Login:{' '}
                {loggedUserData?.user_last_login &&
                  dayjs(loggedUserData.user_last_login).isValid() &&
                  formatDate(loggedUserData.user_last_login, 'MMM DD YYYY')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <ProfileMenu handleClose={handleClose} anchorEl={anchorEl} />

      {switchUsed && (
        <Suspense fallback={null}>
          <SwitchCompanyForm
            isOpen={isOpen}
            setOpen={setOpen}
            initialValues={{
              comp_code: loggedUserCompCode ?? '',
              divn_code: loggedUserDivsnCode ?? '',
              divn_name: loggedUserDivsnName ?? '',
              comp_name: loggedUserCompName ?? '',
            }}
            companyData={companyData}
            divisionData={divisionData}
            getToken={getToken}
            handleClose={handleClose}
          />
        </Suspense>
      )}

      {/* QR Code Modal */}
      <Modal
        open={openQR}
        onClose={() => setOpenQR(false)}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: '90%', sm: '400px' },
            bgcolor: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2.5,
              background: 'linear-gradient(135deg, #1ABC55 0%, #15A349 100%)',
            }}
          >
            <Typography
              sx={{
                color: 'white',
                fontSize: '1.125rem',
                fontFamily: 'inherit',
                fontWeight: 600,
              }}
            >
              Licence Key
            </Typography>
            <IconButton
              onClick={() => setOpenQR(false)}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'inherit',
                fontWeight: 500,
                fontSize: '0.9375rem',
                color: '#64748b',
                mb: 3,
              }}
            >
              Scan the QR code to activate your
              <br />
              <strong style={{ color: '#1e293b' }}>ERConnect Pro</strong>{' '}
              application
            </Typography>

            <Box
              sx={{
                display: 'inline-block',
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
              }}
            >
              <Suspense fallback={<Box sx={{ width: 180, height: 180 }} />}>
                <QRCode value={licKey} size={180} />
              </Suspense>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Fragment>
  );
};

export default HeaderContent;
